type RGBColor = string; // e.g., "119, 209, 215"

export function extendColorPalette(palette: RGBColor[], extendBy: number): RGBColor[] {
  const extendedPalette: RGBColor[] = [...palette];

  // Convert RGB to HSL to generate vibrant colors
  const hslColors = palette.map((rgb) => {
    const [r, g, b] = rgb.split(',').map(Number);
    return rgbToHsl(r, g, b);
  });

  // Generate new vibrant colors by varying the hue
  for (let i = 0; i < extendBy; i++) {
    const lastHsl = hslColors[hslColors.length - 1];
    const newHue = (lastHsl[0] + 30) % 360; // Increment hue by 30 degrees
    const newColor = hslToRgb(newHue, 70, 50); // Keep saturation and lightness high
    extendedPalette.push(newColor);
    hslColors.push([newHue, 70, 50]);
  }

  return extendedPalette;
}

// Helper function to convert RGB to HSL
function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;
  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  let h = 0;
  let s: number;
  const l = (max + min) / 2;

  if (max === min) {
    h = 0;
    s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rNorm:
        h = (gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0);
        break;
      case gNorm:
        h = (bNorm - rNorm) / d + 2;
        break;
      case bNorm:
        h = (rNorm - gNorm) / d + 4;
        break;
    }
    h *= 60;
  }

  return [h, s * 100, l * 100];
}

// Helper function to convert HSL to RGB
function hslToRgb(h: number, s: number, l: number): RGBColor {
  const sNorm = s / 100;
  const lNorm = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sNorm * Math.min(lNorm, 1 - lNorm);
  const f = (n: number) => lNorm - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1));
  const r = Math.round(f(0) * 255);
  const g = Math.round(f(8) * 255);
  const b = Math.round(f(4) * 255);
  return `${r}, ${g}, ${b}`;
}

export function rgbToHex(rgb: string): string {
  const [r, g, b] = rgb.split(',').map(Number);
  // Ensure that each color component is in the valid range (0 to 255)
  const clamp = (value: number) => Math.max(0, Math.min(255, value));

  // Convert each RGB component to a 2-digit hexadecimal string
  const hex = (value: number): string => {
    const hexValue = clamp(value).toString(16).toUpperCase();
    return hexValue.length === 1 ? `0${hexValue}` : hexValue;
  };

  // Return the full hex color code
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}

export function getChartColors(count: number): string[] {
  // Design system base colors (from tailwind.config.js pieChartColor)
  const baseColors = [
    '#0FCA98', // green
    '#D9B64E', // yellow
    '#334D6E', // blue
    '#1F79B8', // navy
    '#F27141', // orange (from previous palette)
    '#FECACA', // light red (optional, fallback)
  ];
  if (count <= baseColors.length) return baseColors.slice(0, count);
  // Generate more colors if needed
  const palette = baseColors.map((hex) => {
    // Convert hex to rgb string for extendColorPalette
    const r = Number.parseInt(hex.slice(1, 3), 16);
    const g = Number.parseInt(hex.slice(3, 5), 16);
    const b = Number.parseInt(hex.slice(5, 7), 16);
    return `${r}, ${g}, ${b}`;
  });
  const extended = extendColorPalette(palette, count - baseColors.length);
  return extended.map(rgbToHex).slice(0, count);
}
